import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface UseCategorySummaryResult {
  categorySummary: string | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch category summary for an assessment with polling.
 * - Polls every 3 seconds if summary is not yet available.
 * - Stops polling after 30 seconds (10 polls) and hides section.
 */
export function useCategorySummary(
  assessmentId: number | null,
  childId?: number | null
): UseCategorySummaryResult {
  const [categorySummary, setCategorySummary] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!assessmentId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check child-level guardrail status first
      if (childId) {
        try {
          const status = await api.getChildSummaryStatus(childId);
          if (status.latestJobStatus === 'FAILED' || status.latestCategoryJobStatus === 'FAILED') {
            setCategorySummary(null);
            setError('FAILED');
            setIsLoading(false);
            return;
          }
        } catch {
          // If status check fails, proceed with assessment fetch
        }
      }

      const assessment = await api.getAssessmentById(assessmentId);

      if (assessment.categorySummary) {
        setCategorySummary(assessment.categorySummary);
        setIsGenerating(false);
      } else {
        // Check if recently completed (within 30s) → likely still generating
        const completedAt = assessment.completedAt ? new Date(assessment.completedAt) : null;
        const secondsSinceCompletion = completedAt
          ? (Date.now() - completedAt.getTime()) / 1000
          : Infinity;

        if (secondsSinceCompletion < 30) {
          setIsGenerating(true);
        } else {
          setCategorySummary(null);
          setIsGenerating(false);
        }
      }

      setIsLoading(false);
    } catch {
      setError('FAILED');
      setIsLoading(false);
    }
  }, [assessmentId, childId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Poll every 3 seconds while generating, max 30 seconds (10 polls)
  useEffect(() => {
    if (!isGenerating || !assessmentId) return;

    let pollCount = 0;
    const maxPolls = 10;

    const pollInterval = setInterval(async () => {
      pollCount++;

      if (pollCount >= maxPolls) {
        setIsGenerating(false);
        setError('FAILED');
        clearInterval(pollInterval);
        return;
      }

      try {
        // Check status endpoint first for early failure detection
        if (childId) {
          const status = await api.getChildSummaryStatus(childId);
          if (status.latestCategoryJobStatus === 'FAILED') {
            setCategorySummary(null);
            setIsGenerating(false);
            setError('FAILED');
            clearInterval(pollInterval);
            return;
          }
        }

        const assessment = await api.getAssessmentById(assessmentId);

        if (assessment.categorySummary) {
          setCategorySummary(assessment.categorySummary);
          setIsGenerating(false);
          clearInterval(pollInterval);
        }
      } catch {
        // Polling errors are silent — keep trying
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isGenerating, assessmentId, childId]);

  return { categorySummary, isLoading, isGenerating, error, refetch: fetchSummary };
}
